"use client"

/**
 * OffersV2 UI Components
 * Visual components for displaying Amazon deal badges, savings, and offer details
 * (uses React hooks — must be a Client Component so server pages can import the
 * badges barrel that re-exports it).
 */

import { Badge } from "@/components/ui/badge"
import { Clock, Zap, Crown, AlertCircle, Package } from "lucide-react"
import { useEffect, useState } from "react"

// Deal Badge Component
export function DealBadge({ 
  dealBadge, 
  dealType, 
  dealAccessType 
}: { 
  dealBadge?: string | null
  dealType?: string | null
  dealAccessType?: string | null
}) {
  if (!dealBadge) return null

  const isLightningDeal = dealType === 'LIGHTNING_DEAL'
  const isPrimeExclusive = dealAccessType === 'PRIME_EXCLUSIVE'
  const isPrimeEarlyAccess = dealAccessType === 'PRIME_EARLY_ACCESS'

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {isLightningDeal && (
        <Badge variant="destructive" className="bg-orange-600 hover:bg-orange-700 text-white font-semibold">
          <Zap className="w-3 h-3 mr-1" />
          {dealBadge}
        </Badge>
      )}
      {isPrimeExclusive && (
        <Badge variant="default" className="bg-blue-600 hover:bg-blue-700 text-white font-semibold">
          <Crown className="w-3 h-3 mr-1" />
          Prime Exclusive
        </Badge>
      )}
      {isPrimeEarlyAccess && (
        <Badge variant="default" className="bg-purple-600 hover:bg-purple-700 text-white font-semibold">
          <Crown className="w-3 h-3 mr-1" />
          Prime Early Access
        </Badge>
      )}
      {/* FRIDGE-141: green-700 — white text on green-600 measured 3.22:1. */}
      {!isLightningDeal && !isPrimeExclusive && !isPrimeEarlyAccess && dealBadge && (
        <Badge variant="secondary" className="bg-green-700 hover:bg-green-800 text-white font-semibold">
          {dealBadge}
        </Badge>
      )}
    </div>
  )
}

// Deal Countdown Timer
export function DealCountdown({ dealEndsAt }: { dealEndsAt?: string | null }) {
  const [timeLeft, setTimeLeft] = useState<string>('')

  useEffect(() => {
    if (!dealEndsAt) return

    const updateTimer = () => {
      const now = new Date().getTime()
      const end = new Date(dealEndsAt).getTime()
      const distance = end - now

      if (distance < 0) {
        setTimeLeft('Deal ended')
        return
      }

      const days = Math.floor(distance / (1000 * 60 * 60 * 24))
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60))
      const seconds = Math.floor((distance % (1000 * 60)) / 1000)

      if (days > 0) {
        setTimeLeft(`${days}d ${hours}h ${minutes}m`)
      } else if (hours > 0) {
        setTimeLeft(`${hours}h ${minutes}m ${seconds}s`)
      } else {
        setTimeLeft(`${minutes}m ${seconds}s`)
      }
    }

    updateTimer()
    const interval = setInterval(updateTimer, 1000)

    return () => clearInterval(interval)
  }, [dealEndsAt])

  if (!dealEndsAt || !timeLeft) return null

  return (
    <div className="flex items-center gap-2 text-sm text-orange-600 font-medium">
      <Clock className="w-4 h-4" />
      <span>Ends in {timeLeft}</span>
    </div>
  )
}

// Deal Progress Bar
export function DealProgress({ percentClaimed }: { percentClaimed?: number | null }) {
  if (!percentClaimed) return null

  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs text-gray-600">
        <span>{percentClaimed}% claimed</span>
        <span>{100 - percentClaimed}% remaining</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
        <div 
          className="bg-orange-500 h-2 transition-all duration-300"
          style={{ width: `${percentClaimed}%` }}
        />
      </div>
    </div>
  )
}

// Savings Percentage Badge
export function SavingsPercentageBadge({ 
  savingsPercentage,
  savingsBasisLabel 
}: { 
  savingsPercentage?: number | null
  savingsBasisLabel?: string | null
}) {
  if (!savingsPercentage) return null

  return (
    <div className="inline-flex items-center gap-2">
      <Badge variant="outline" className="bg-red-50 border-red-200 text-red-700 font-bold text-base px-3 py-1">
        Save {savingsPercentage}%
      </Badge>
      {savingsBasisLabel && (
        <span className="text-sm text-gray-500">off {savingsBasisLabel}</span>
      )}
    </div>
  )
}

// Low Stock Warning
export function LowStockWarning({ 
  availabilityType,
  availabilityMessage,
  availabilityMaxQty
}: { 
  availabilityType?: string | null
  availabilityMessage?: string | null
  availabilityMaxQty?: number | null
}) {
  const isLowStock = availabilityType === 'IN_STOCK_SCARCE'
  const isOutOfStock = availabilityType === 'OUT_OF_STOCK'
  const isPreorder = availabilityType === 'PREORDER'

  if (!isLowStock && !availabilityMessage && !isPreorder && !isOutOfStock) return null

  return (
    <div className={`flex items-center gap-2 p-3 rounded-lg ${
      isOutOfStock ? 'bg-red-50 text-red-700' :
      isLowStock ? 'bg-orange-50 text-orange-700' :
      isPreorder ? 'bg-blue-50 text-blue-700' :
      'bg-gray-50 text-gray-700'
    }`}>
      <AlertCircle className="w-4 h-4 flex-shrink-0" />
      <div className="flex-1">
        {availabilityMessage && (
          <p className="text-sm font-medium">{availabilityMessage}</p>
        )}
        {availabilityMaxQty && (
          <p className="text-xs mt-1">Limit {availabilityMaxQty} per order</p>
        )}
      </div>
    </div>
  )
}

// Buy Box Winner Indicator
export function BuyBoxWinnerBadge({ isBuyBoxWinner }: { isBuyBoxWinner?: boolean }) {
  if (!isBuyBoxWinner) return null

  return (
    <Badge variant="outline" className="bg-blue-50 border-blue-200 text-blue-700 font-semibold">
      <Crown className="w-3 h-3 mr-1" />
      Featured Offer
    </Badge>
  )
}

// Merchant Info
export function MerchantInfo({ 
  merchantName,
  amazonFulfilled 
}: { 
  merchantName?: string | null
  amazonFulfilled?: boolean 
}) {
  if (!merchantName) return null

  return (
    <div className="flex items-center gap-2 text-sm text-gray-600">
      <Package className="w-4 h-4" />
      <span>
        Sold by <span className="font-medium text-gray-900">{merchantName}</span>
        {amazonFulfilled && (
          <span className="ml-1 text-blue-600">• Fulfilled by Amazon</span>
        )}
      </span>
    </div>
  )
}

// Price Per Unit Display
export function PricePerUnit({ pricePerUnit }: { pricePerUnit?: string | null }) {
  if (!pricePerUnit) return null

  return (
    <p className="text-sm text-[#697080]">
      ({pricePerUnit})
    </p>
  )
}

// Complete Offer Card with all OffersV2 features
export function EnhancedOfferCard({ offer }: { offer: any }) {
  return (
    <div className="border rounded-lg p-4 space-y-3 hover:border-blue-500 transition-colors">
      {/* Header with retailer and badges */}
      <div className="flex items-start justify-between gap-2">
        <div className="space-y-1">
          <h3 className="font-semibold text-lg">{offer.retailer}</h3>
          <BuyBoxWinnerBadge isBuyBoxWinner={offer.isBuyBoxWinner} />
        </div>
        <DealBadge 
          dealBadge={offer.dealBadge}
          dealType={offer.dealType}
          dealAccessType={offer.dealAccessType}
        />
      </div>

      {/* Price and Savings */}
      <div className="space-y-2">
        <div className="flex items-baseline gap-3">
          <span className="text-3xl font-bold text-gray-900">
            {offer.priceFormatted}
          </span>
          {offer.originalPriceFormatted && (
            <span className="text-lg text-gray-500 line-through">
              {offer.originalPriceFormatted}
            </span>
          )}
        </div>
        
        {offer.savingsPercentage && (
          <SavingsPercentageBadge 
            savingsPercentage={offer.savingsPercentage}
            savingsBasisLabel={offer.savingsBasisLabel}
          />
        )}
        
        {offer.savingsFormatted && !offer.savingsPercentage && (
          <p className="text-green-600 font-medium">
            Save {offer.savingsFormatted}
          </p>
        )}

        <PricePerUnit pricePerUnit={offer.pricePerUnit} />
      </div>

      {/* Deal Info */}
      {offer.dealEndsAt && (
        <div className="space-y-2">
          <DealCountdown dealEndsAt={offer.dealEndsAt} />
          <DealProgress percentClaimed={offer.dealPercentClaimed} />
        </div>
      )}

      {/* Availability */}
      <LowStockWarning 
        availabilityType={offer.availabilityType}
        availabilityMessage={offer.availabilityMessage}
        availabilityMaxQty={offer.availabilityMaxQty}
      />

      {/* Merchant */}
      <MerchantInfo 
        merchantName={offer.merchantName}
        amazonFulfilled={offer.amazonFulfilled}
      />

      {/* Prime/Shipping */}
      {(offer.isPrime || offer.freeShipping) && (
        <div className="flex items-center gap-3 text-sm">
          {offer.isPrime && (
            <Badge variant="secondary" className="bg-blue-100 text-blue-700">
              Prime
            </Badge>
          )}
          {offer.freeShipping && (
            <span className="text-green-600 font-medium">Free Shipping</span>
          )}
        </div>
      )}

      {/* CTA Button */}
      <button className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors">
        View at {offer.retailer}
      </button>
    </div>
  )
}

